import { type ReactNode } from "react";
import { Check, Pencil } from "lucide-react";
import { cn } from "@/lib/utils";

// =============================================================================
// WizardCard
//
// Building block for the step-by-step trip-request flow. Each step in
// `TripForm` renders a single `<WizardCard>` whose visual state matches
// where the user is in the wizard:
//
//   - pending  → nothing rendered (the user hasn't reached this step; the
//                live summary rail lists what is still to come)
//   - active   → a raised card with the step number, title, description and
//                the step's inputs
//   - complete → a row on the step timeline: a tick on the connecting line,
//                the step label, the answer, and an Edit button.
//
// Consecutive complete rows draw one continuous vertical line through their
// nodes (the `before:` pseudo-element), so the answered steps read as a
// timeline leading down into the active card.
// =============================================================================

export type WizardStepStatus = "pending" | "active" | "complete";

interface WizardCardProps {
  /** Step number shown in the leading badge (1-based for users). */
  index: number;
  /** Short heading. */
  title: string;
  /** Optional subtitle shown under the heading on the active card. */
  description?: string;
  /** Current state — drives which body to render. */
  status: WizardStepStatus;
  /** One-line value summary rendered in the collapsed/complete state.
   *  Falls back to "—" if empty so the row never looks broken. */
  summary?: ReactNode;
  /** Called when the user clicks the Edit button on a complete step. */
  onEdit?: () => void;
  /** Disable the Edit affordance — used for steps whose answer must not
   *  be changed in place (e.g. the patient-lookup step requires going
   *  through the dedicated Reset button). */
  editDisabled?: boolean;
  /** Active-card body. */
  children?: ReactNode;
}

export function WizardCard({
  index,
  title,
  description,
  status,
  summary,
  onEdit,
  editDisabled,
  children,
}: WizardCardProps) {
  if (status === "pending") return null;

  if (status === "complete") {
    return (
      <article
        className={cn(
          "animate-slide-up group relative flex items-center gap-3.5 rounded-xl border border-transparent px-3 py-2.5 transition-colors",
          "hover:border-border/60 hover:bg-foreground/[0.025]",
          // The timeline line: runs through this row's node and into the
          // gaps above and below, joining the neighbouring rows' lines.
          "before:absolute before:left-7 before:top-[-0.75rem] before:bottom-[-0.75rem] before:w-px before:-translate-x-1/2 before:bg-border",
          "first:before:top-1/2",
        )}
      >
        <span className="relative z-10 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-background text-success ring-1 ring-success/45">
          <Check className="h-3.5 w-3.5" strokeWidth={3} />
        </span>
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="text-[0.68rem] font-bold uppercase tracking-[0.12em] text-muted-foreground">
            {String(index).padStart(2, "0")} · {title}
          </span>
          <span className="line-clamp-2 break-words text-[15px] font-semibold text-foreground sm:line-clamp-1">
            {summary ?? "—"}
          </span>
        </div>
        {onEdit && !editDisabled ? (
          <button
            type="button"
            onClick={onEdit}
            aria-label={`Edit step ${index}: ${title}`}
            className="inline-flex h-8 flex-shrink-0 items-center gap-1.5 rounded-lg px-2.5 text-xs font-semibold text-muted-foreground transition hover:bg-primary/10 hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Pencil className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Edit</span>
          </button>
        ) : null}
      </article>
    );
  }

  // Active.
  return (
    <article
      data-wizard-active="true"
      className={cn(
        "animate-slide-up relative rounded-2xl border border-primary/25 bg-card/70 p-5 ring-1 ring-primary/10 backdrop-blur-xl sm:p-7",
        "shadow-[inset_0_1px_0_0_hsl(0_0%_100%/0.05),0_24px_60px_-30px_hsl(var(--primary)/0.55)]",
        // grid-cols-[minmax(0,1fr)] makes grid items shrinkable below
        // their content's intrinsic size — without this, a single wide
        // child (chip group, side-by-side buttons, an unbreakable
        // address) would push the whole card past the viewport on
        // mobile.
        "grid grid-cols-[minmax(0,1fr)] gap-5 scroll-mt-32 [&>*]:min-w-0",
      )}
    >
      <header className="flex items-start gap-3.5">
        <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary to-[hsl(var(--accent-2))] text-sm font-bold text-primary-foreground shadow-[0_6px_16px_-6px_hsl(var(--primary)/0.8)]">
          {index}
        </span>
        <div className="flex min-w-0 flex-col gap-0.5 pt-0.5">
          {/* tabIndex=-1 so TripForm's focus-on-step-change effect can
              programmatically focus this for screen-reader announcement
              without exposing it in the natural tab order. */}
          <h2
            tabIndex={-1}
            className="text-lg font-bold leading-tight tracking-tight focus:outline-none sm:text-xl"
          >
            {title}
          </h2>
          {description ? (
            <p className="text-sm text-muted-foreground">{description}</p>
          ) : null}
        </div>
      </header>
      <div className="grid grid-cols-[minmax(0,1fr)] gap-4 [&>*]:min-w-0">
        {children}
      </div>
    </article>
  );
}
